// Sistema de logging estructurado y configurable

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export interface LogContext {
  module?: string;
  action?: string;
  userId?: string;
  documentId?: string;
  [key: string]: unknown;
}

class Logger {
  private level: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.level = this.getLogLevel();
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  private getLogLevel(): LogLevel {
    const level = process.env.NEXT_PUBLIC_LOG_LEVEL || 'INFO';
    return LogLevel[level as keyof typeof LogLevel] ?? LogLevel.INFO;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.level;
  }

  private formatMessage(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` | ${JSON.stringify(context)}` : '';
    return `[${timestamp}] ${level}: ${message}${contextStr}`;
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG) && this.isDevelopment) {
      console.debug(this.formatMessage('DEBUG', message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage('INFO', message, context));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message, context));
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorContext = {
        ...context,
        error: error?.message,
        stack: this.isDevelopment ? error?.stack : undefined,
      };
      console.error(this.formatMessage('ERROR', message, errorContext));
    }
  }

  // Métodos específicos por módulo
  auth = {
    login: (userId: string) => this.info('Usuario autenticado', { module: 'auth', action: 'login', userId }),
    logout: (userId: string) => this.info('Usuario cerró sesión', { module: 'auth', action: 'logout', userId }),
    error: (message: string, error?: Error) => this.error(message, error, { module: 'auth' }),
  };

  documents = {
    created: (documentId: string, userId: string) =>
      this.info('Documento creado', { module: 'documents', action: 'create', documentId, userId }),
    signed: (documentId: string, userId: string) =>
      this.info('Documento firmado', { module: 'documents', action: 'sign', documentId, userId }),
    error: (message: string, error?: Error, documentId?: string) => this.error(message, error, { module: 'documents', documentId }),
  };

  api = {
    request: (method: string, url: string) => this.debug('HTTP Request', { module: 'api', method, url }),
    response: (status: number, url: string) => this.debug('HTTP Response', { module: 'api', status, url }),
    error: (message: string, error?: Error, url?: string) => this.error(message, error, { module: 'api', url }),
  };
}

export const logger = new Logger();

// Helper para migración gradual
export const createModuleLogger = (module: string) => ({
  debug: (message: string, context?: Omit<LogContext, 'module'>) => logger.debug(message, { ...context, module }),
  info: (message: string, context?: Omit<LogContext, 'module'>) => logger.info(message, { ...context, module }),
  warn: (message: string, context?: Omit<LogContext, 'module'>) => logger.warn(message, { ...context, module }),
  error: (message: string, error?: Error, context?: Omit<LogContext, 'module'>) => logger.error(message, error, { ...context, module }),
});
